import { useQuery } from 'react-query'
import QUERIES from 'api/queries'
import {
	getCurrentUser,
	getFeatureAccess,
	getJurisdictionBySlug,
	getJurisdictionProperties,
	getOnboardingStatus,
	getSupportedJurisdictions,
	getTutorialStatus,
	getUserById,
	getWealthicaCredentials,
} from './services'
import { IUser } from './models'
import { useLocalization } from 'localization'
import { useCallback, useMemo } from 'react'
import http, { CLIENT_ID_KEY } from 'api/http'
import { TransactionType } from 'api/transactions/dtos'
import { useAppContext } from 'modules/common/appContext'

export const useClientId = () =>
	useMemo(() => {
		const storageValue = http.getStorageItem(CLIENT_ID_KEY)
		return storageValue && storageValue !== 'undefined' ? storageValue : undefined
	}, [])

export const useCurrentUser = (enabled = true) => {
	const clientId = useClientId()
	const { wealthicaContext, isWealthicaContext } = useAppContext()

	const query = useQuery(QUERIES.current, () => (clientId ? getUserById(clientId) : getCurrentUser()), {
		onSuccess: ({ email }) => {
			if (isWealthicaContext && wealthicaContext?.authUser?.email && email !== wealthicaContext.authUser.email) {
				http.logout()
			}
		},
		onError: ({ response }) => {
			// Ignore logout when CPA is viewing clients data and out of credits
			if (response && response.status !== 403) {
				http.logout(true)
			}
		},
		enabled,
	})

	// override value for wealthica user
	if (query.data?.email && wealthicaContext?.paidUntil) {
		query.data.paidUntil = wealthicaContext.paidUntil
	}

	return {
		...query,
		clientId,
		isCPAview: !!query.data?.isCPA && !clientId, // global Layout CPA vs trader
		cpaClientView: !query.data?.isCPA && !!clientId, // CPA is viewing non-CPA account
		cpaOwnView: !!clientId && query.data?.id === clientId, // CPA is viewing own account
	}
}

export const useUserById = (userId: IUser['id']) =>
	useQuery([QUERIES.current, userId], () => getUserById(userId), {
		enabled: !!userId,
	})

export const useFeatureAccess = () => useQuery(QUERIES.featureAccess, getFeatureAccess)

export const useJurisdictions = () => {
	const { t } = useLocalization()
	const query = useQuery(QUERIES.jurisdictions, getSupportedJurisdictions)

	return {
		...query,
		jurisdictionOptions: query.data?.map(({ shortName }) => ({
			label: t(`settings.jurisdictions.${shortName}`),
			value: shortName,
		})),
	}
}

export const useJurisdictionBySlug = (slug: string) =>
	useQuery(QUERIES.jurisdictionBySlug, () => getJurisdictionBySlug(slug))

export const useJurisdictionProperties = (slug?: string) => {
	const { t } = useLocalization()
	const query = useQuery(
		[QUERIES.jurisdictionProperties, slug],
		() => (slug ? getJurisdictionProperties(slug) : undefined),
		{
			enabled: !!slug,
		}
	)
	return {
		...query,
		userProperties: useMemo(
			() =>
				query.data?.user
					? Object.keys(query.data.user).map(key => ({
							title: t(`settings.properties.${key}`),
							key,
							options: query.data?.user![key].map(value => ({
								label: t(`settings.properties.${value}`),
								value,
							})),
					  }))
					: [],
			[query.data, t]
		),
		walletProperties: useMemo(
			() =>
				query.data?.wallet
					? Object.keys(query.data.wallet).map(key => ({
							title: t(`settings.properties.${key}`),
							key,
							options: query.data?.wallet![key].map(value => ({
								label: t(`settings.properties.${value}`),
								value,
							})),
					  }))
					: [],
			[query.data, t]
		),
		getTransactionProperties: useCallback(
			(transactionType: TransactionType) =>
				query.data?.transcaction?.[transactionType]
					? Object.keys(query.data.transcaction[transactionType]!).map(key => ({
							title: t(`settings.properties.${key}`),
							key,
							options: query.data?.transcaction[transactionType]![key].map(value => ({
								label: t(`settings.properties.${value}`),
								value,
							})),
					  }))
					: [],
			[query.data, t]
		),
	}
}

export const useOnboarding = (enabled: boolean) =>
	useQuery(QUERIES.onboarding, getOnboardingStatus, {
		enabled,
	})

export const useTutorialStatus = (enabled = true) => {
	const clientId = useClientId()

	const query = useQuery(QUERIES.tutorials, getTutorialStatus, { enabled: !clientId && enabled })
	return query
}

export const useGetWealthicaUserCredentials = (email?: IUser['email'], enabled = true) =>
	useQuery(
		[QUERIES.wealthicaUserCredentials, email],
		async () => {
			if (!email) return

			return await getWealthicaCredentials(email)
		},
		{
			enabled: enabled && !!email,
		}
	)
